import React from "react";
import { useTranslation } from "react-i18next";

const UserDetails = ({ user, onClose, onEdit }) => {
  const { t } = useTranslation(["users", "common"]);

  const getGenderIcon = (gender) => {
    switch (gender) {
      case "MALE":
        return "fa-mars text-primary";
      case "FEMALE":
        return "fa-venus text-danger";
      default:
        return "fa-genderless text-secondary";
    }
  };

  const getGenderLabel = (gender) => {
    return t(`users:gender.${gender}`, { defaultValue: gender });
  };

  const getEducationLevelLabel = (level) => {
    return t(`users:educationLevel.${level}`, { defaultValue: String(level) });
  };

  const getStatusBadge = (active, banned, emailVerified) => {
    return (
      <div className="d-flex gap-2">
        {active ? (
          <span className="badge bg-success">
            <i className="fas fa-check-circle me-1"></i>
            {t("users:status.active")}
          </span>
        ) : (
          <span className="badge bg-secondary">
            <i className="fas fa-pause-circle me-1"></i>
            {t("users:status.inactive")}
          </span>
        )}
        {banned ? (
          <span className="badge bg-danger">
            <i className="fas fa-ban me-1"></i>
            {t("users:status.banned")}
          </span>
        ) : null}
        {emailVerified ? (
          <span className="badge bg-info">
            <i className="fas fa-envelope-circle-check me-1"></i>
            {t("users:status.emailVerified")}
          </span>
        ) : (
          <span className="badge bg-warning">
            <i className="fas fa-envelope me-1"></i>
            {t("users:status.emailNotVerified")}
          </span>
        )}
      </div>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      ROLE_UNIVERSITY_ADMIN: { color: "danger", icon: "fa-crown" },
      ROLE_ADMIN: { color: "danger", icon: "fa-crown" },
      ROLE_MODERATOR: { color: "warning", icon: "fa-shield" },
      ROLE_USER: { color: "info", icon: "fa-user" },
    };

    const config = roleConfig[role] || roleConfig["ROLE_USER"];

    return (
      <span className={`badge bg-${config.color}`}>
        <i className={`fas ${config.icon} me-1`}></i>
        {t(`users:roles.${role}`, { defaultValue: role })}
      </span>
    );
  };

  return (
    <div className="user-details">
      <div className="row">
        <div className="col-md-8">
          {/* User Header */}
          <div className="d-flex align-items-center mb-4">
            <div
              className="user-avatar rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
              style={{ width: "80px", height: "80px" }}
            >
              <i className="fas fa-user text-primary fs-2"></i>
            </div>
            <div>
              <h3 className="fw-bold text-dark mb-1">{user.fullName}</h3>
              <p className="text-muted mb-2">{user.email}</p>
              <div className="d-flex gap-2 flex-wrap">
                <span className="badge bg-info fs-6">
                  <i className="fas fa-id-card me-1"></i>
                  {user.studentNumber}
                </span>
                {getRoleBadge(user.role)}
                {getStatusBadge(user.active, user.banned, user.emailVerified)}
              </div>
            </div>
          </div>

          {/* Academic Hierarchy */}
          <div className="card border-0 bg-light mb-4">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <i className="fas fa-graduation-cap me-2 text-primary"></i>
                {t("users:details.academicInfo")}
              </h6>
              <div className="user-hierarchy">
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-university text-primary me-2"></i>
                  <span className="fw-medium">{t("users:fields.university")}:</span>
                  <span className="ms-2">{user.universityName || "N/A"}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-building text-info me-2"></i>
                  <span className="fw-medium">{t("users:fields.campus")}:</span>
                  <span className="ms-2">{user.campusName || "N/A"}</span>
                </div>
                {user.facultyName && (
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-graduation-cap text-success me-2"></i>
                    <span className="fw-medium">{t("users:fields.faculty")}:</span>
                    <span className="ms-2">{user.facultyName}</span>
                  </div>
                )}
                {user.departmentName && (
                  <div className="d-flex align-items-center">
                    <i className="fas fa-sitemap text-warning me-2"></i>
                    <span className="fw-medium">{t("users:fields.department")}:</span>
                    <span className="ms-2">{user.departmentName}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold text-muted small">
                {t("users:details.userId")}
              </label>
              <p className="text-dark fs-5">{user.id}</p>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold text-muted small">
                {t("users:fields.studentNumber").toUpperCase()}
              </label>
              <p className="text-dark fs-5">{user.studentNumber}</p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold text-muted small">
                {t("users:fields.email").toUpperCase()}
              </label>
              <p className="text-dark">{user.email}</p>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold text-muted small">
                {t("users:fields.phoneNumber").toUpperCase()}
              </label>
              <p className="text-dark">{user.phoneNumber || "N/A"}</p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold text-muted small">
                {t("users:fields.gender").toUpperCase()}
              </label>
              <p className="text-dark">
                <i className={`fas ${getGenderIcon(user.gender)} me-2`}></i>
                {getGenderLabel(user.gender)}
              </p>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold text-muted small">
                {t("users:fields.educationLevel").toUpperCase()}
              </label>
              <p className="text-dark">
                <i className="fas fa-graduation-cap me-2"></i>
                {getEducationLevelLabel(user.educationLevel)}
              </p>
            </div>
          </div>

        </div>

        {/* Statistics Panel */}
        <div className="col-md-4">
          <div className="card border-0 bg-light h-100">
            <div className="card-body">
              {/* Account Status */}
              <div>
                <h6 className="fw-bold text-muted small mb-3">
                  {t("users:details.accountStatus")}
                </h6>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">{t("users:details.emailVerifiedLabel")}</span>
                  <span
                    className={`fw-medium ${
                      user.emailVerified ? "text-success" : "text-danger"
                    }`}
                  >
                    <i
                      className={`fas ${
                        user.emailVerified ? "fa-check" : "fa-times"
                      } me-1`}
                    ></i>
                    {user.emailVerified ? t("common:common.yes") : t("common:common.no")}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">{t("users:details.accountActiveLabel")}</span>
                  <span
                    className={`fw-medium ${
                      user.active ? "text-success" : "text-secondary"
                    }`}
                  >
                    <i
                      className={`fas ${
                        user.active ? "fa-check" : "fa-pause"
                      } me-1`}
                    ></i>
                    {user.active ? t("common:common.yes") : t("common:common.no")}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">{t("users:details.bannedLabel")}</span>
                  <span
                    className={`fw-medium ${
                      user.banned ? "text-danger" : "text-success"
                    }`}
                  >
                    <i
                      className={`fas ${
                        user.banned ? "fa-ban" : "fa-check"
                      } me-1`}
                    ></i>
                    {user.banned ? t("common:common.yes") : t("common:common.no")}
                  </span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-top pt-3 mt-3">
                <h6 className="fw-bold text-muted small mb-3">{t("users:details.quickActions")}</h6>
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      onClose();
                      onEdit(user);
                    }}
                  >
                    <i className="fas fa-edit me-2"></i>
                    {t("users:editUser")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
